import { useState, useRef, useEffect } from 'react';
import { Badge, Button, ConfirmModal, TextButton } from '@safetyhub/ui';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore, NotificationType, Notification } from '../stores/notification';
import { useInvitationStore } from '../stores/invitation';
import { OrganizationInvitation, getInvitationKindLabel } from '../lib/organizations';
import { useAuthStore } from '../stores/auth';
import { useToastStore } from '../stores/toast';
import { useActionItemAcknowledge, useActionItems } from '../stores/actionItems';
import SiteTransferAcceptModal from './modals/SiteTransferAcceptModal';
import {
  LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE,
  LEGAL_OBLIGATION_SITE_RESOURCE_TYPE,
  legalObligationInstancePath,
  legalSiteObligationsPath,
} from '../lib/legalDocumentRoutes';
import { AGENCY_LINK_REQUEST_RESOURCE_TYPE, agencyLinkRequestPath } from '../lib/agencySiteLinkView';

type TabType = 'notifications' | 'invitations';

// 알림 타입별 아이콘 및 색상
const notificationConfig: Record<NotificationType, { icon: React.ReactNode; bgColor: string }> = {
  site_assigned: {
    icon: <BuildingPlusIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  site_unassigned: {
    icon: <BuildingMinusIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  site_updated: {
    icon: <BuildingEditIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  site_primary_needed: {
    icon: <AlertIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  site_assignment_changed: {
    icon: <SwapIcon className="h-5 w-5 text-violet-600" />,
    bgColor: 'bg-violet-100',
  },
  member_invited: {
    icon: <UserPlusIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  member_joined: {
    icon: <UserCheckIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  member_left: {
    icon: <UserMinusIcon className="h-5 w-5 text-ink-600" />,
    bgColor: 'bg-ink-100',
  },
  member_role_changed: {
    icon: <UserCogIcon className="h-5 w-5 text-violet-600" />,
    bgColor: 'bg-violet-100',
  },
  organization_restored: {
    icon: <RefreshIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  company_merge_request: {
    icon: <SwapIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  company_merge_accepted: {
    icon: <BuildingPlusIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  company_merge_rejected: {
    icon: <AlertIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  inspection_created: {
    icon: <ClipboardCheckIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  inspection_contributor_joined_room: {
    icon: <UserPlusIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  inspection_all_reviews_done: {
    icon: <ClipboardCheckIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  inspection_modified: {
    icon: <PencilIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  inspection_contributor_joined: {
    icon: <UserPlusIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  inspection_soft_deleted: {
    icon: <TrashIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  inspection_restored: {
    icon: <RefreshIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  inspection_completed: {
    icon: <CheckCircleIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  inspection_review_requested: {
    icon: <EyeIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  report_created: {
    icon: <DocumentIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  report_deleted: {
    icon: <DocumentIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  invitation_accepted: {
    icon: <UserCheckIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  invitation_rejected: {
    icon: <UserMinusIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  invitation_sent: {
    icon: <UserPlusIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  invitation_cancelled: {
    icon: <UserMinusIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  report_submitted: {
    icon: <DocumentIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  report_submission_cancelled: {
    icon: <DocumentIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  ai_analysis_started: {
    icon: <SparklesIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  ai_analysis_completed: {
    icon: <SparklesIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  ai_analysis_failed: {
    icon: <SparklesIcon className="h-5 w-5 text-rose-600" />,
    bgColor: 'bg-rose-100',
  },
  pdf_generated: {
    icon: <DocumentIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  verification_completed: {
    icon: <CheckCircleIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  verification_mismatch: {
    icon: <AlertIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  action_success: {
    icon: <CheckCircleIcon className="h-5 w-5 text-emerald-600" />,
    bgColor: 'bg-emerald-100',
  },
  info: {
    icon: <InfoIcon className="h-5 w-5 text-ink-600" />,
    bgColor: 'bg-ink-100',
  },
  dlr_draft_completed: {
    icon: <DocumentIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  // 법정 서류 기한 임박 (줄기 항목 3) — ⛔ 이 줄이 빠지면 `notificationConfig[type] || …info`가
  // **조용히** 일반 아이콘으로 접는다(red 가 없다). 톤은 `lib/notifications.ts`의 amber 와 맞춘다.
  legal_obligation_due_soon: {
    icon: <AlertIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  // 사건 등록 취소(9-o) — 할 일이 사라졌다는 안내라 중립 톤(`lib/notifications.ts`의 slate 와 맞춘다)
  legal_obligation_event_revoked: {
    icon: <InfoIcon className="h-5 w-5 text-ink-600" />,
    bgColor: 'bg-ink-100',
  },
  // 대행 현장 연결(P4) — 톤은 `lib/notifications.ts`와 맞춘다(요청 = 업체가 처리할 할 일이라 amber)
  site_agency_linked: {
    icon: <BuildingPlusIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  site_agency_unlinked: {
    icon: <BuildingMinusIcon className="h-5 w-5 text-ink-600" />,
    bgColor: 'bg-ink-100',
  },
  site_agency_link_requested: {
    icon: <AlertIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  site_agency_link_resolved: {
    icon: <InfoIcon className="h-5 w-5 text-blue-600" />,
    bgColor: 'bg-blue-100',
  },
  // 대행 기관 탈퇴 예정(L32 결정 ①) — 유예 안에 내려받아야 하는 조치라 amber(`lib/notifications.ts`의 톤과 맞춘다)
  legal_obligation_agency_closing: {
    icon: <AlertIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
  phone_removed: {
    icon: <AlertIcon className="h-5 w-5 text-amber-600" />,
    bgColor: 'bg-amber-100',
  },
};

// 시간 포맷팅
function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return '방금 전';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}분 전`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}시간 전`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}일 전`;

  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * 메시지에서 배지 정보 파싱
 * 패턴: "[조직명][현장명] 메시지", "[조직명] 메시지", "'현장명' 메시지"
 */
function parseBadgesFromMessage(message: string): {
  badges?: { orgName?: string; siteName?: string };
  cleanMessage: string;
} {
  // 패턴 1: [조직명][현장명] 또는 [조직명]
  const bracketRegex = /^\[([^\]]+)\](?:\[([^\]]+)\])?\s*/;
  const bracketMatch = message.match(bracketRegex);

  if (bracketMatch) {
    const orgName = bracketMatch[1];
    const siteName = bracketMatch[2];
    const cleanMessage = message.replace(bracketRegex, '').trim();
    return {
      badges: { orgName, siteName: siteName || undefined },
      cleanMessage,
    };
  }

  // 패턴 2: '현장명' 또는 '조직명' (작은따옴표)
  const quoteRegex = /^'([^']+)'\s*/;
  const quoteMatch = message.match(quoteRegex);

  if (quoteMatch) {
    const name = quoteMatch[1];
    const cleanMessage = message.replace(quoteRegex, '').trim();
    // 조직/현장 구분을 위해 메시지 내용 확인
    if (message.includes('조직')) {
      return {
        badges: { orgName: name },
        cleanMessage,
      };
    }
    return {
      badges: { siteName: name },
      cleanMessage,
    };
  }

  return { cleanMessage: message };
}

/** 배지 컴포넌트 */
function NotificationBadges({ badges }: { badges: { orgName?: string; siteName?: string } }) {
  if (!badges.orgName && !badges.siteName) return null;

  return (
    <span className="inline-flex items-center gap-1 mr-1">
      {badges.orgName && (
        <Badge tone="blue" shape="rounded" size="sm">
          {badges.orgName}
        </Badge>
      )}
      {badges.siteName && (
        <Badge tone="emerald" shape="rounded" size="sm">
          {badges.siteName}
        </Badge>
      )}
    </span>
  );
}

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('notifications');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [transferToken, setTransferToken] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotificationStore();
  const { invitations, openModal: openInvitationModal, acceptInvitation, rejectInvitation } = useInvitationStore();
  const { fetchUser, setCurrentOrganization } = useAuthStore();
  const { showToast } = useToastStore();
  const [processingToken, setProcessingToken] = useState<string | null>(null);
  /**
   * 확인이 필요한 항목 — **상단 바와 같은 정본**(`useActionItems`)을 읽는다. 두 표면이 각자
   * 목록을 만들면 한쪽만 갱신되는 드리프트가 생긴다(결정 기록 2항).
   */
  const actionItems = useActionItems();
  /**
   * 실행·busy·실패 토스트도 **상단 바와 같은 한 벌**이다 — 문구를 두 표면에 복사해 두면
   * 갈린다(실제로 갈려 있었다: 2026-10-05 리뷰, 바는 `catch {}`로 조용했다).
   */
  const { busyItemId: busyActionItemId, run: runActionItemAcknowledge } = useActionItemAcknowledge();

  // 전체 삭제 확인
  const handleClearAllClick = () => {
    setShowClearConfirm(true);
  };

  const handleClearAllConfirm = () => {
    clearAll();
    setShowClearConfirm(false);
  };

  const handleClearAllCancel = () => {
    setShowClearConfirm(false);
  };

  /**
   * 총 알림 수 (일반 알림 + 초대).
   *
   * ⚠️ **확인이 필요한 항목은 여기 더하지 않는다**(팀장 판단 2026-09-29) — 항목은 상단 바가
   * 이미 **상시 노출**하므로 벨 배지까지 세면 같은 사실이 두 숫자에 겹쳐 잡힌다. 배지는
   * 「벨을 열어야 알 수 있는 것」의 개수라는 뜻을 유지한다.
   */
  const totalCount = unreadCount + invitations.length;

  // 드롭다운 위치 계산 (fixed position 기반)
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 320; // w-80 = 20rem = 320px
      const dropdownHeight = 448; // max-h-[28rem] = 28rem = 448px
      const padding = 8; // 여백

      // 수평 위치 계산
      const spaceRight = window.innerWidth - buttonRect.left;
      let left: number;
      if (spaceRight >= dropdownWidth + padding) {
        // 버튼 왼쪽 기준으로 오른쪽에 표시
        left = buttonRect.left;
      } else {
        // 버튼 오른쪽 기준으로 왼쪽에 표시
        left = buttonRect.right - dropdownWidth;
      }
      // 화면 밖으로 나가지 않도록 보정
      left = Math.max(padding, Math.min(left, window.innerWidth - dropdownWidth - padding));

      // 수직 위치 계산
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      let top: number;
      if (spaceBelow >= dropdownHeight + padding) {
        // 버튼 아래에 표시
        top = buttonRect.bottom + padding;
      } else {
        // 버튼 위에 표시
        top = buttonRect.top - dropdownHeight - padding;
      }

      setDropdownStyle({ top, left });
    }
  }, [isOpen]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // 버튼이나 드롭다운 클릭이 아닌 경우에만 닫기
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    setIsOpen(false);

    // resourceType/resourceId 기반 네비게이션 (새 방식)
    const resourceType = notification.data?.resourceType as string | undefined;
    const resourceId = notification.data?.resourceId as string | undefined;

    if (resourceType && resourceId) {
      switch (resourceType) {
        // 단일 리소스
        case 'inspection':
          navigate(`/inspections/${resourceId}`);
          return;
        case 'report':
          navigate(`/reports/${resourceId}`);
          return;
        case 'site':
          navigate(`/sites/${resourceId}`);
          return;
        case 'approval':
          navigate(`/approvals/${resourceId}`);
          return;
        case LEGAL_OBLIGATION_INSTANCE_RESOURCE_TYPE:
          // 법정 서류 기한 임박 — 알림은 인스턴스 id만 싣는다. 경로 정본은 `lib/legalDocumentRoutes.ts`
          // (알림 목록 페이지 `getNotificationResourcePath`·실시간 토스트·SW와 같은 함수를 읽는다)
          navigate(legalObligationInstancePath(resourceId));
          return;
        case LEGAL_OBLIGATION_SITE_RESOURCE_TYPE:
          // 사건 등록 취소 — resourceId = 현장 id(인스턴스는 이미 지워졌다) · 그 현장의 의무 목록
          navigate(legalSiteObligationsPath(resourceId));
          return;
        case AGENCY_LINK_REQUEST_RESOURCE_TYPE:
          // 대행 연결 요청·처리(P4) — 경로 정본은 `lib/agencySiteLinkView.ts`
          navigate(agencyLinkRequestPath(resourceId));
          return;
        case 'tbm':
          navigate(`/reports/${resourceId}`);
          return;
        case 'SITE_TRANSFER':
          if (resourceId) {
            setTransferToken(resourceId);
          }
          return;
        case 'company_merge_request':
          // 세 알림이 같은 resourceType·같은 토큰을 달고 오므로 목적지는 알림 타입으로 갈린다.
          // 수락 화면은 대상 조직 멤버만 열 수 있어(그 외에는 403), 요청을 *받은* 쪽만 보낸다.
          // 수락/거절 통지는 요청한 쪽이 받고 그때 요청은 이미 PENDING이 아니라 화면이 열리지
          // 않으므로, 진행 상태를 보는 자리인 조직 설정으로 보낸다.
          if (notification.type === 'company_merge_request') {
            navigate(`/company-merge/${resourceId}`);
          } else {
            navigate('/settings/organization');
          }
          return;
        case 'organization':
          // 조직 설정 페이지로 이동
          navigate(`/settings/organization`);
          return;
        case 'profile':
          // 프로필 설정 페이지로 이동
          navigate(`/settings/profile`);
          return;
        case 'invitation':
          // 초대 알림 - 초대 드롭다운에서 처리되므로 별도 이동 없음
          return;
        // 목록 리소스 (resourceId는 조직 ID)
        case 'sites':
          navigate(`/sites`);
          return;
        case 'inspections':
          navigate(`/inspections`);
          return;
        case 'members':
          navigate(`/settings/members`);
          return;
        case 'organizations':
          // 조직 탈퇴 후 - 대시보드로 이동
          navigate(`/dashboard`);
          return;
        /**
         * 이 switch가 모르는 타입은 **서버가 정한 목적지**로 보낸다(2026-09-08).
         *
         * 종전엔 여기서 `console.warn` 하고 끝이라 알림을 눌러도 아무 일이 없었다 — 읽음
         * 처리만 되고 화면은 그대로다. 「내가 직접 진행」 TBM 교육 리마인더
         * (`resourceType: 'education'`)가 정확히 그 자리에 떨어졌다: 그 알림의 **유일한**
         * 도달 경로인데 벨에서 누르면 죽었다(2026-09-08 dev 실측에서 적발).
         *
         * 폴백을 위 case들보다 **뒤에** 두는 이유 — `actionUrl`을 앞세우면 기존 알림의
         * 목적지가 바뀐다. 결재 알림의 `actionUrl`은 외부 결재자용 토큰 링크
         * (`/approval/<token>`)라 이 switch의 결재함(`/approvals/<id>`)과 다른 곳이고,
         * 프로필 알림도 `/profile` vs `/settings/profile`로 갈린다. 어느 쪽이 옳은지는
         * 별도 판단이 필요하므로 **모르는 타입만** 서버에 맡긴다.
         *
         * 이 값이 store까지 오게 하는 것이 짝이 되는 변경이다 — `convertServerNotification`이
         * `actionUrl`을 버리고 있었다(`stores/notification.ts` 주석 참조). 둘 중 하나만
         * 고치면 여전히 죽는다.
         */
        default: {
          const serverPath = notification.data?.actionUrl;
          if (typeof serverPath === 'string' && serverPath.startsWith('/')) {
            navigate(serverPath);
            return;
          }
          console.warn(`Unknown resourceType: ${resourceType}`);
        }
      }
    }

    // 기존 방식 (하위 호환)
    // 보고서 관련 알림 클릭 시 보고서 상세로 이동
    if (notification.data?.reportId) {
      navigate(`/reports/${notification.data.reportId}`);
      return;
    }

    // 점검 관련 알림 클릭 시 점검 상세로 이동
    if (notification.data?.inspectionId) {
      // scrollTo 파라미터가 있으면 포함
      const scrollParam = notification.data.scrollTo ? `?scrollTo=${notification.data.scrollTo}` : '';
      navigate(`/inspections/${notification.data.inspectionId}${scrollParam}`);
      return;
    }
  };

  // 초대 수락 처리
  const handleAcceptInvitation = async (invitation: OrganizationInvitation) => {
    setProcessingToken(invitation.token);
    try {
      await acceptInvitation(invitation.token);
      // 토스트는 서버에서 WebSocket ACTION_RESULT로 전송됨
      await fetchUser();
      const invCompanyId = invitation.companyId ?? invitation.subscription?.company?.id ?? '';
      if (invCompanyId) setCurrentOrganization(invCompanyId);
    } catch {
      showToast('초대 수락에 실패했습니다.', 'error');
    } finally {
      setProcessingToken(null);
    }
  };

  // 초대 거절 처리
  const handleRejectInvitation = async (invitation: OrganizationInvitation) => {
    setProcessingToken(invitation.token);
    try {
      await rejectInvitation(invitation.token);
      // 토스트는 서버에서 WebSocket ACTION_RESULT로 전송됨
    } catch {
      showToast('초대 거절에 실패했습니다.', 'error');
    } finally {
      setProcessingToken(null);
    }
  };

  /**
   * 초대가 있고 **볼 것이 알림 탭에 없으면** 초대 탭으로 자동 전환한다.
   *
   * ⚠️ `actionItems.length === 0`이 빠지면 아래 「확인이 필요한 항목」 섹션이 **열자마자
   * 가려진다** — 그 섹션은 알림 탭 **안**에 있고(결정 기록 개정 1, 사용자 결정 2026-09-29의
   * 근거가 「기본 탭이 `notifications`라 벨을 열면 즉시 보인다」다), 항목은 알림 행을 만들지
   * 않으므로 `unreadCount === 0`이 **흔한** 조합이다. 즉 「내 중복 인원 보유 + 조직 초대 1건」
   * 하나로 결정 기록의 「두 표면」 중 하나가 도달 불가가 된다(2026-10-05 리뷰 적발).
   *
   * 초대를 못 보게 되는 것은 아니다 — 초대 탭 배지가 개수를 계속 띄우고 벨 배지의
   * `totalCount`에도 실린다. 자동 전환은 **알림 탭이 빈 경우의 편의**일 뿐이다.
   */
  useEffect(() => {
    if (isOpen && invitations.length > 0 && unreadCount === 0 && actionItems.length === 0) {
      setActiveTab('invitations');
    }
  }, [isOpen, invitations.length, unreadCount, actionItems.length]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 알림 버튼 */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-card-sm text-ink-500 hover:bg-surface-hover transition-colors"
        title="알림"
      >
        <NotificationBellIcon className="h-6 w-6" />
        {totalCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-xs text-white font-medium">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {/* 드롭다운 - Portal로 body에 렌더링하여 z-index 문제 해결 */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="fixed w-80 bg-white rounded-card shadow-card border border-line z-[9999] max-h-[28rem] flex flex-col"
        >
          {/* 탭 헤더 */}
          <div className="border-b border-line">
            <div className="flex">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`flex-1 px-4 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
                  activeTab === 'notifications'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
              >
                알림
                {unreadCount > 0 && (
                  <Badge tone="rose" size="sm" className="ml-1.5 justify-center min-w-[22px] tabular-nums">
                    {unreadCount}
                  </Badge>
                )}
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`flex-1 px-4 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
                  activeTab === 'invitations'
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
              >
                초대
                {invitations.length > 0 && (
                  <Badge tone="blue" size="sm" className="ml-1.5 justify-center min-w-[22px] tabular-nums">
                    {invitations.length}
                  </Badge>
                )}
              </button>
            </div>
          </div>

          {/* 탭 컨텐츠 */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'notifications' ? (
              <>
                {/*
                  확인이 필요한 항목 — 알림 탭 **안 상단 섹션**(결정 기록 개정 1, 사용자 결정
                  2026-09-29). 기본 탭이 `notifications`라 벨을 열면 즉시 보인다. 0건이면 섹션째 없다.
                  ⚠️ 초대 탭은 이 작업의 범위가 아니다(개정 1에 되짚을 조건이 있다).
                  ⚠️ 「벨을 열면 즉시 보인다」는 **공짜가 아니다** — 위 초대 자동 전환 effect가
                  `actionItems.length === 0`을 함께 보지 않으면 이 섹션이 열자마자 가려진다
                  (2026-10-05 리뷰). 그 조건을 지우면 아래 spec의 「초대가 있어도 …」가 깨진다.
                */}
                {actionItems.length > 0 && (
                  <div
                    className="px-4 py-3 border-b border-line bg-amber-50/60"
                    // 문구 분리 가드가 **실제로 렌더된** bellLabel을 읽는 손잡이다.
                    // 하드코딩한 기대 문자열로 비교하면 bellLabel이 barLabel과 같아졌을 때
                    // 그 문자열이 어디에도 없어 단언이 **공허하게 통과**한다(2026-09-29 적발).
                    data-testid="bell-action-items"
                  >
                    <p className="text-xs font-semibold text-amber-800 mb-2">확인이 필요한 항목</p>
                    <ul className="space-y-2">
                      {actionItems.map((item) => (
                        <li key={item.id} data-testid="bell-action-item">
                          {/*
                            문구는 `bellLabel` — 상단 바(`barLabel`)와도, 아래 알림 목록의 문구와도
                            다르다(결정 기록 4항: 같아지면 이 설계는 실패다). 항목은 **지금 참인
                            상태**를, 알림은 **지나간 이벤트**를 말한다.
                          */}
                          <p className="text-sm text-ink-800">{item.bellLabel}</p>
                          <div className="mt-1 flex items-center gap-3">
                            <TextButton
                              className="text-xs"
                              onClick={() => {
                                // 조치는 이동(프로필) 또는 모달 열기다 — 둘 다 드롭다운 밖에서
                                // 일어나므로 닫는다.
                                setIsOpen(false);
                                item.cta.run();
                              }}
                            >
                              {item.cta.label}
                            </TextButton>
                            {item.acknowledge && (
                              <TextButton
                                tone="neutral"
                                className="text-xs"
                                disabled={busyActionItemId === item.id}
                                onClick={() => runActionItemAcknowledge(item)}
                              >
                                {item.acknowledge.label}
                              </TextButton>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 알림 탭 헤더 액션 */}
                {(unreadCount > 0 || notifications.length > 0) && (
                  <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-line-soft">
                    {unreadCount > 0 && (
                      <TextButton onClick={markAllAsRead} className="text-xs">
                        모두 읽음
                      </TextButton>
                    )}
                    {notifications.length > 0 && (
                      <TextButton tone="neutral" onClick={handleClearAllClick} className="text-xs">
                        전체 삭제
                      </TextButton>
                    )}
                  </div>
                )}

                {/* 알림 목록 */}
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-ink-500">
                    <NotificationBellIcon className="h-12 w-12 text-ink-300 mb-2" />
                    <p className="text-sm">알림이 없습니다</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-line-soft">
                    {notifications.map((notification) => {
                      const config = notificationConfig[notification.type] || notificationConfig.info;
                      const resourceType = notification.data?.resourceType as string | undefined;
                      const hasNavigation = !!(
                        resourceType !== 'invitation' && (
                          notification.data?.resourceId ||
                          notification.data?.inspectionId ||
                          notification.data?.reportId
                        )
                      );
                      return (
                        <li
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`px-4 py-3 hover:bg-surface-hover cursor-pointer transition-colors ${
                            /* 안읽음 강조는 brand — blue는 타입 아이콘 분류색이라 겹치면 상태와 타입이 섞인다(알림 페이지와 같은 결정) */
                            !notification.read ? 'bg-brand-50/50' : ''
                          }`}
                        >
                          <div className="flex gap-3">
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
                              {config.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm ${!notification.read ? 'font-medium text-ink-900' : 'text-ink-700'}`}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-brand-600 mt-1.5" />
                                )}
                              </div>
                              <p className="text-sm text-ink-500">
                                {(() => {
                                  const { badges, cleanMessage } = parseBadgesFromMessage(notification.message);
                                  return (
                                    <>
                                      {badges && <NotificationBadges badges={badges} />}
                                      {cleanMessage}
                                    </>
                                  );
                                })()}
                              </p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-xs text-ink-400">{formatTimeAgo(notification.createdAt)}</p>
                                {hasNavigation && (
                                  <span className="text-xs text-brand-500">클릭하여 이동 →</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            ) : (
              <>
                {/* 초대 목록 */}
                {invitations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-ink-500">
                    <MailIcon className="h-12 w-12 text-ink-300 mb-2" />
                    <p className="text-sm">대기 중인 초대가 없습니다</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-line-soft">
                    {invitations.map((invitation) => (
                      <li key={invitation.id} className="px-4 py-3">
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <MailPlusIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-ink-900 truncate">
                                {invitation.subscription?.company?.name ?? invitation.company?.name ?? invitation.organization?.name ?? ''}
                              </p>
                              <Badge tone="blue" shape="rounded">
                                {getInvitationKindLabel(invitation)}
                              </Badge>
                            </div>
                            {invitation.site && (
                              <p className="text-xs text-ink-500 truncate">
                                현장: {invitation.site.name}
                              </p>
                            )}
                            <p className="text-xs text-ink-400 mt-0.5">
                              {invitation.inviter.name}님이 초대
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Button
                                variant="primary"
                                size="xs"
                                onClick={() => handleAcceptInvitation(invitation)}
                                disabled={processingToken === invitation.token}
                              >
                                {processingToken === invitation.token ? '처리 중...' : '수락'}
                              </Button>
                              <Button
                                variant="secondary"
                                size="xs"
                                onClick={() => handleRejectInvitation(invitation)}
                                disabled={processingToken === invitation.token}
                              >
                                거절
                              </Button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {/* 전체 보기 버튼 */}
                {invitations.length > 0 && (
                  <div className="px-4 py-2 border-t border-line-soft">
                    <TextButton
                      onClick={() => {
                        setIsOpen(false);
                        openInvitationModal();
                      }}
                      className="w-full text-center text-xs"
                    >
                      전체 보기
                    </TextButton>
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* 현장 이관 수락 모달 */}
      <SiteTransferAcceptModal
        isOpen={!!transferToken}
        onClose={() => setTransferToken(null)}
        token={transferToken ?? ''}
      />

      {/* 전체 삭제 확인 모달 — 층은 `z-[10000]`을 유지한다: 이 확인은 드롭다운(`z-[9999]`, 위 패널)이
          열린 채로 뜨므로 modalLayers의 root(`z-[70]`)로 내리면 드롭다운이 확인 위를 덮는다.
          danger 규약이라 자동 포커스가 「취소」 — Enter 오조작으로 알림이 지워지지 않는다. */}
      <ConfirmModal
        open={showClearConfirm}
        title="알림 전체 삭제"
        body={
          <p className="text-sm text-ink-600">
            모든 알림을 삭제하시겠습니까?<br />
            삭제된 알림은 복구할 수 없습니다.
          </p>
        }
        confirmLabel="삭제"
        danger
        overlayZClassName="z-[10000]"
        onConfirm={handleClearAllConfirm}
        onClose={handleClearAllCancel}
      />
    </div>
  );
}

// 아이콘 컴포넌트들
function NotificationBellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function BuildingPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v6m3-3H9" />
    </svg>
  );
}

function BuildingMinusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6" />
    </svg>
  );
}

function BuildingEditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 6l3 3m0 0l-7 7H8v-3l7-7z" />
    </svg>
  );
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    </svg>
  );
}

function UserCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11l2 2 4-4" />
    </svg>
  );
}

function UserMinusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"
      />
    </svg>
  );
}

function UserCogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M19.5 12.5a.5.5 0 100-1 .5.5 0 000 1z"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function SwapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
      />
    </svg>
  );
}

function ClipboardCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function MailPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v6m3-3H9" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}
